import {LocatedNumbers} from "../../common/utils/locatedNumbers";
import {resolve} from "../../container";
import {Timeout} from "../../common/timeout";
import {IntersectionObserverFactory, onceIntersected} from "../../common/observation";
import {ACTIVATION_CHANGE, ActivationChangeEventParams, KEYS, prepareEvents} from "../../common/utils/events";
import {ManagingResources} from "../../common/lifetime";
import {GLOBAL} from "../../common/globals";
import type {Nullable} from "../../bootstrap/common/types";
import {UnLitElement} from "../../common/elements";
import {html, type PropertyValues, svg, type TemplateResult} from "lit";
import {customElement, property, query, queryAll, state} from "lit/decorators.js";


const TOTAL_WIDTH = 650;
const HALF_TOTAL_WIDTH = TOTAL_WIDTH / 2;
const CIRCUMFERENCE = 1000;
const RADIUS = CIRCUMFERENCE / (2 * Math.PI);
const INITIAL_OFFSET = 625;
const SEPARATOR_WIDTH = 2;
const INACTIVE_STROKE_WIDTH_PERCENTAGE = 30;
const ACTIVE_STROKE_WIDTH_PERCENTAGE = 46;
const FOCUS_DELAY = 1250;
const SVG_VIEW_BOX = `0 0 ${TOTAL_WIDTH} ${TOTAL_WIDTH}`;
const SVG_GROUP_ROLE = "group";
const SVG_SLOT = "svg";
const OUTER_CIRCLE_CLASS = "donut-chart-outer-circle reverse-background";
const ANIMATION_CIRCLE_CLASS = "donut-chart-animation-circle reverse-background";
const INNER_CIRCLE_CLASS = "donut-chart-inner-circle";
const SECTOR_ROLE = "img";

export class DonutChartSector {
    private length: number;

    public constructor(
        public id: string,
        public dataLabel: string,
        public legendLabel: string,
        public value: number,
        public valueLabel: string,
        public hint: string,
        public colorClass: string,
        relativeLength: number,
        public offset: number,
        public effectiveCircumference: number
    ) {
        this.length = relativeLength * this.effectiveCircumference;
    }

    public static from(
        config: SectorConfig,
        index: number,
        insertPosition: number,
        totalSectorCount: number,
        totalValue: number,
        unit: string,
        locatedNumbers: LocatedNumbers,
        colorClasses: string[]
    ): DonutChartSector {
        return new DonutChartSector(
            index.toString(),
            config.dataLabel,
            config.legendLabel,
            config.value,
            config.customValueLabel || locatedNumbers.formatNumber(config.value) + " " + unit,
            config.hint,
            config.colorClass || colorClasses[index % colorClasses.length],
            config.value / totalValue,
            insertPosition,
            CIRCUMFERENCE - totalSectorCount * SEPARATOR_WIDTH
        );
    }

    public getLength(): number {
        return this.length;
    }

    public calculateAriaLabel(): string {
        return `${this.dataLabel}: ${this.valueLabel}`;
    }
}

export class DonutChart {
    public sectors: DonutChartSector[];

    private constructor() {
        this.sectors = [];
    }

    public static from(sectorConfigs: SectorConfig[], unit: string, locatedNumbers: LocatedNumbers, colorClasses: string[]): DonutChart {
        let currentInsertPosition = INITIAL_OFFSET;
        const donutChart = new DonutChart();
        const totalValue = sectorConfigs.map(sector => sector.value)
            .reduce((sum, value) => sum + value, 0);

        sectorConfigs.forEach((sectorConfig, index) => {
            const chartSector = DonutChartSector.from(
                sectorConfig,
                index,
                currentInsertPosition,
                sectorConfigs.length,
                totalValue,
                unit,
                locatedNumbers,
                colorClasses
            );

            donutChart.sectors.push(chartSector);
            currentInsertPosition += chartSector.getLength() + SEPARATOR_WIDTH;
        });
        return donutChart;
    }

    public getSectorWithId(id?: string): Nullable<DonutChartSector> {
        return this.sectors.find(sector => sector.id === id) ?? null;
    }

    public totalValue(): number {
        return this.sectors.map(sector => sector.value)
            .reduce((sum, value) => sum + value, 0);
    }
}

export type SectorConfig = {
    dataLabel: string;
    legendLabel: string;
    value: number;
    customValueLabel: string;
    hint: string;
    colorClass: string;
};

@customElement("eop-donut-chart")
export class EopDonutChart extends ManagingResources(UnLitElement) {
    @property({attribute: "donut-sectors", type: Array})
    private sectorsConfig: SectorConfig[] = [];
    @property()
    private unit: string = "";
    @property({attribute: "donut-caption"})
    private caption: Nullable<string>;
    @property({attribute: "donut-colors", type: Array})
    private colorClasses: string[] = [];

    @state()
    private hint: string = "";
    @state()
    private valueLabel: string = "";
    @state()
    private dataLabel: string = "";
    @state()
    private focusRingVisible: boolean = false;

    private chart: DonutChart;

    private focusedSector: Nullable<DonutChartSector>;
    private pointerFocusedSector: Nullable<DonutChartSector>;

    @query(".donut-chart-svg")
    private donutChartSvg: HTMLElement;
    @query(".donut-chart-animation-circle")
    private animationCircle: SVGCircleElement;
    @queryAll(".donut-chart-sector")
    private sectorElements: NodeListOf<SVGElement>;

    private intersectionObserver: IntersectionObserver;

    public constructor(
        private locatedNumbers: LocatedNumbers = resolve(LocatedNumbers),
        private timeout: Timeout = resolve(Timeout),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        if (this.sectorsConfig.isEmpty()) {
            return;
        }
        this.chart = DonutChart.from(this.sectorsConfig, this.unit, this.locatedNumbers, this.colorClasses);

        prepareEvents(GLOBAL.window())
            .boundTo(this)
            .on(ACTIVATION_CHANGE, (ev: CustomEvent<ActivationChangeEventParams>) => {
                if (ev.detail.affects(this)) {
                    this.handleViewportIntersection();
                }
            });
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.intersectionObserver = this.intersectionObserverFactory.create(
            onceIntersected(() => this.handleViewportIntersection()), {threshold: .5});

        this.intersectionObserver.observe(this.donutChartSvg);
    }

    public render(): Nullable<TemplateResult> {
        if (!this.chart) {
            return null;
        }

        return html`
            <div class="donut-chart-flex-container">
                <div class="donut-chart-svg-container">
                    <div class="donut-chart-svg">
                        <div class="donut-chart-label">
                            <div class="donut-chart-hint" data-container-slot="hint">${this.hint}</div>
                            <div class="donut-chart-value-label" data-container-slot="valueLabel">${this.valueLabel}</div>
                            <div class="donut-chart-data-label" data-container-slot="dataLabel">${this.dataLabel}</div>
                        </div>
                        ${this.renderChart()}
                    </div>
                    ${this.renderCaption()}
                </div>
                <div class="donut-chart-legend" data-container-slot="legend">${this.legendElements()}</div>
            </div>
        `;
    }

    private renderCaption(): TemplateResult {
        if (this.caption) {
            return html`
                <div class="donut-caption" data-slot="caption">${this.caption}</div>`;
        }
        return html`
            <div class="donut-caption" data-slot="caption" style="display: none;"></div>`;
    }

    private renderChart(): TemplateResult {
        const activeStrokeWidthPct = ACTIVE_STROKE_WIDTH_PERCENTAGE / 100;
        const focusRingInnerRadius = Math.round(RADIUS + activeStrokeWidthPct * HALF_TOTAL_WIDTH) + 4;
        const focusRingOuterRadius = focusRingInnerRadius + 6;
        const sectors = this.chart.sectors.map((sector, index) => this.renderSector(sector, index));
        return svg`
            <svg viewBox=${SVG_VIEW_BOX} role=${SVG_GROUP_ROLE} data-slot=${SVG_SLOT}>
                <circle r=${RADIUS} cx=${HALF_TOTAL_WIDTH} cy=${HALF_TOTAL_WIDTH} class=${OUTER_CIRCLE_CLASS}></circle>
                ${sectors}
                <circle r=${RADIUS} cx=${HALF_TOTAL_WIDTH} cy=${HALF_TOTAL_WIDTH} class=${ANIMATION_CIRCLE_CLASS}></circle>
                <circle r=${RADIUS} cx=${HALF_TOTAL_WIDTH} cy=${HALF_TOTAL_WIDTH} class=${INNER_CIRCLE_CLASS} ></circle>
                <circle r=${focusRingOuterRadius} cx=${HALF_TOTAL_WIDTH} cy=${HALF_TOTAL_WIDTH} class=${this.focusRingClass("donut-chart-focus-ring-outer")}></circle>
                <circle r=${focusRingInnerRadius} cx=${HALF_TOTAL_WIDTH} cy=${HALF_TOTAL_WIDTH} class=${this.focusRingClass("donut-chart-focus-ring-inner")}></circle>
            </svg>
        `;
    }

    private renderSector(sector: DonutChartSector, index: number): TemplateResult {
        const active = this.focusedSector?.id === sector.id;
        const tabindex = active || (!this.focusedSector && index === 0) ? "0" : "-1";

        return svg`<circle
            r=${RADIUS}
            cx=${TOTAL_WIDTH / 2}
            cy=${TOTAL_WIDTH / 2}
            stroke-dashoffset=${-sector.offset}
            stroke-dasharray=${`${sector.getLength()} ${CIRCUMFERENCE - sector.getLength()}`}
            stroke-width=${`${active ? ACTIVE_STROKE_WIDTH_PERCENTAGE : INACTIVE_STROKE_WIDTH_PERCENTAGE}%`}
            class=${`donut-chart-sector ${sector.colorClass}`}
            role=${SECTOR_ROLE}
            aria-label=${sector.calculateAriaLabel()}
            tabindex=${tabindex}
            @mouseenter=${() => this.handleSectorMouseEnter(sector)}
            @pointerdown=${() => this.handleSectorPointerDown(sector)}
            @keydown=${(event: KeyboardEvent) => this.handleSectorKeydown(event, index)}
            @focus=${() => this.handleSectorFocus(sector)}
            @blur=${this.hideFocusRing}
        ></circle>`;
    }

    private handleViewportIntersection(): void {
        if (!this.donutChartSvg.isVisible()) {
            return;
        }
        this.initializeChart();
    }

    private initializeChart(): void {
        this.animationCircle.classList.add("animate");
        this.timeout.delay(() => {
            if (this.focusedSector) {
                return;
            }
            this.focusSector(this.chart.sectors[0]);
        }, FOCUS_DELAY);
    }

    private legendElements(): TemplateResult[] {
        return this.chart.sectors.map(sector => html`
            <div class="legend-entry ${sector.colorClass}">${sector.legendLabel}</div>`);
    }

    private focusRingClass(className: string): string {
        return this.focusRingVisible ? `${className} visible` : className;
    }

    private handleSectorMouseEnter(sector: DonutChartSector): void {
        this.hideFocusRing();
        this.focusSector(sector);
    }

    private handleSectorPointerDown(sector: DonutChartSector): void {
        this.pointerFocusedSector = sector;
    }

    private handleSectorFocus(sector: DonutChartSector): void {
        this.focusSector(sector);
        if (this.pointerFocusedSector !== sector) {
            this.showFocusRing();
        }
        this.pointerFocusedSector = null;
    }

    private showFocusRing(): void {
        this.focusRingVisible = true;
    }

    private hideFocusRing(): void {
        this.focusRingVisible = false;
    }

    private focusSector(sector: DonutChartSector): void {
        this.focusedSector = sector;
        this.setChartLabel(sector);
    }

    private handleSectorKeydown(event: KeyboardEvent, currentIndex: number): void {
        const sectorElements = Array.from(this.sectorElements);
        let nextIndex: number | undefined;

        if (event.key === KEYS.ARROW_RIGHT || event.key === KEYS.ARROW_DOWN) {
            nextIndex = (currentIndex + 1) % sectorElements.length;
        }
        if (event.key === KEYS.ARROW_LEFT || event.key === KEYS.ARROW_UP) {
            nextIndex = (currentIndex - 1 + sectorElements.length) % sectorElements.length;
        }

        if (nextIndex !== undefined) {
            event.preventDefault();
            const nextElement = sectorElements[nextIndex];
            this.focusSector(this.chart.sectors[nextIndex]);
            nextElement.focus();
        }
    }

    private setChartLabel(sector: DonutChartSector): void {
        this.hint = sector.hint;
        this.valueLabel = sector.valueLabel;
        this.dataLabel = sector.dataLabel;
    }
}